import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Server, Plus, Trash2, RefreshCw, CheckCircle, AlertCircle, ExternalLink,
  Zap, TrendingDown, Cpu, HardDrive, Copy, Play, Sparkles, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GEMMA4_MODELS = [
  {
    id: "gemma-4-e2b-it",
    name: "Gemma 4 E2B",
    params: "5.1B (2.3B effective)",
    vram: "~10 GB",
    gpu: "RTX 3090+",
    context: "128K",
    quality: 80,
    recommended: false,
    description: "Efficient model for on-device deployment",
  },
  {
    id: "gemma-4-e4b-it",
    name: "Gemma 4 E4B",
    params: "8B (4.5B effective)",
    vram: "~16 GB",
    gpu: "RTX 4090",
    context: "128K",
    quality: 83,
    recommended: false,
    description: "Enhanced multimodal capabilities",
  },
  {
    id: "gemma-4-27b-it",
    name: "Gemma 4 27B MoE",
    params: "25.2B (3.8B active)",
    vram: "~30-60 GB",
    gpu: "A100 80GB",
    context: "256K",
    quality: 90,
    recommended: true,
    description: "Best cost/quality ratio - runs at 4B speed",
  },
  {
    id: "gemma-4-31b-it",
    name: "Gemma 4 31B Dense",
    params: "30.7B",
    vram: "~71 GB",
    gpu: "H100 SXM5",
    context: "256K",
    quality: 93,
    recommended: false,
    description: "Highest quality for complex reasoning",
  },
];

export default function CustomEndpointsManager() {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [savingsData, setSavingsData] = useState(null);
  const [gemma4Models, setGemma4Models] = useState([]);
  const [healthChecking, setHealthChecking] = useState({});
  const [testing, setTesting] = useState({});

  // Add endpoint form
  const [newEndpoint, setNewEndpoint] = useState({
    name: "",
    endpoint_url: "",
    api_key: "",
    model_id: "google/gemma-4-27b-it",
    qnneal_model_id: "gemma-4-27b-it",
    gpu_type: "A100",
    cost_per_million_input: 0.03,
    cost_per_million_output: 0.08,
    thinking_mode: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [endpointsRes, savingsRes, modelsRes] = await Promise.all([
        axios.get(`${API}/custom-endpoints`).catch(() => ({ data: { endpoints: [] } })),
        axios.get(`${API}/custom-endpoints/gemma4/savings-estimate?current_model=gpt-4o&monthly_tokens=10000000`).catch(() => ({ data: null })),
        axios.get(`${API}/gemma4/models`).catch(() => ({ data: { models: [] } })),
      ]);
      setEndpoints(endpointsRes.data.endpoints || []);
      setSavingsData(savingsRes.data);
      setGemma4Models(modelsRes.data.models || []);
    } catch (e) {
      console.error("Failed to load custom endpoints:", e);
    }
    setLoading(false);
  };

  const handleAddEndpoint = async () => {
    if (!newEndpoint.name || !newEndpoint.endpoint_url) {
      toast.error("Name and endpoint URL are required");
      return;
    }

    try {
      await axios.post(`${API}/custom-endpoints`, newEndpoint);
      toast.success("Custom endpoint added successfully!");
      setAddDialogOpen(false);
      setNewEndpoint({
        name: "",
        endpoint_url: "",
        api_key: "",
        model_id: "google/gemma-4-27b-it",
        qnneal_model_id: "gemma-4-27b-it",
        gpu_type: "A100",
        cost_per_million_input: 0.03,
        cost_per_million_output: 0.08,
        thinking_mode: false,
      });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add endpoint");
    }
  };

  const handleDeleteEndpoint = async (endpointId) => {
    if (!window.confirm("Are you sure you want to delete this endpoint?")) return;
    try {
      await axios.delete(`${API}/custom-endpoints/${endpointId}`);
      toast.success("Endpoint deleted");
      loadData();
    } catch (e) {
      toast.error("Failed to delete endpoint");
    }
  };

  const handleHealthCheck = async (endpointId) => {
    setHealthChecking((prev) => ({ ...prev, [endpointId]: true }));
    try {
      const res = await axios.post(`${API}/custom-endpoints/${endpointId}/health`);
      toast.success(`Health check: ${res.data.status}`);
      loadData();
    } catch (e) {
      toast.error("Health check failed");
    }
    setHealthChecking((prev) => ({ ...prev, [endpointId]: false }));
  };

  const handleTestEndpoint = async (endpointId) => {
    setTesting((prev) => ({ ...prev, [endpointId]: true }));
    try {
      const res = await axios.post(`${API}/custom-endpoints/${endpointId}/test`);
      if (res.data.success) {
        toast.success(`Test passed! Response: "${res.data.response?.slice(0, 50)}..."`);
      } else {
        toast.error(`Test failed: ${res.data.error}`);
      }
    } catch (e) {
      toast.error("Test request failed");
    }
    setTesting((prev) => ({ ...prev, [endpointId]: false }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-[var(--qn-primary)]" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Gemma 4 Highlight Banner */}
      <Card className="bg-gradient-to-r from-[var(--qn-primary)]/10 to-cyan-500/10 border-[var(--qn-primary)]/20">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-6 h-6 text-[var(--qn-primary)]" />
                <h3 className="text-lg font-semibold">Gemma 4 — Save up to 97% on AI Costs</h3>
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Apache 2.0</Badge>
              </div>
              <p className="text-sm text-[var(--qn-foreground-muted)] mb-4">
                Google DeepMind's most capable open-source model family. The 27B MoE variant matches GPT-4o quality while costing 97% less to run self-hosted.
              </p>
              {savingsData?.best_recommendation && (
                <div className="flex items-center gap-4 p-3 bg-[var(--qn-background)] rounded-lg border border-[var(--qn-border)]">
                  <TrendingDown className="w-5 h-5 text-emerald-400" />
                  <div>
                    <div className="text-sm font-medium">
                      Switch from {savingsData.current_model} to {savingsData.best_recommendation.model}
                    </div>
                    <div className="text-xs text-[var(--qn-foreground-muted)]">
                      Save <span className="text-emerald-400 font-semibold">${savingsData.best_recommendation.monthly_savings}/mo</span> ({savingsData.best_recommendation.savings_percentage}% reduction)
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="shrink-0">
              <Button
                onClick={() => setAddDialogOpen(true)}
                className="btn-primary"
                data-testid="add-gemma4-endpoint-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Gemma 4 Endpoint
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gemma 4 Model Cards */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cpu className="w-5 h-5 text-[var(--qn-primary)]" />
            Gemma 4 Model Family
          </CardTitle>
          <CardDescription className="text-[var(--qn-foreground-muted)]">
            Choose the right model for your workload. All models use Apache 2.0 license.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {GEMMA4_MODELS.map((model) => (
              <div
                key={model.id}
                className={`p-4 rounded-lg border transition-all ${
                  model.recommended
                    ? "bg-[var(--qn-primary)]/5 border-[var(--qn-primary)]/30"
                    : "bg-[var(--qn-background)] border-[var(--qn-border)] hover:border-[var(--qn-primary)]/30"
                }`}
                data-testid={`gemma4-model-${model.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{model.name}</span>
                  {model.recommended && (
                    <Badge className="bg-[var(--qn-primary)]/10 text-[var(--qn-primary)] text-xs">Recommended</Badge>
                  )}
                </div>
                <p className="text-xs text-[var(--qn-foreground-muted)] mb-3">{model.description}</p>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-[var(--qn-foreground-subtle)]">Parameters:</span>
                    <span>{model.params}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--qn-foreground-subtle)]">VRAM:</span>
                    <span>{model.vram}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--qn-foreground-subtle)]">Min GPU:</span>
                    <span>{model.gpu}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--qn-foreground-subtle)]">Context:</span>
                    <span>{model.context}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[var(--qn-border)]">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[var(--qn-foreground-subtle)]">Quality Score</span>
                    <span className="text-sm font-medium text-[var(--qn-primary)]">{model.quality}/100</span>
                  </div>
                  <Progress value={model.quality} className="h-1.5 mt-1" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Configured Endpoints */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-[var(--qn-secondary)]" />
              Custom Endpoints
            </CardTitle>
            <CardDescription className="text-[var(--qn-foreground-muted)]">
              Self-hosted model endpoints configured for QNNEAL routing
            </CardDescription>
          </div>
          <Button variant="outline" onClick={() => setAddDialogOpen(true)} data-testid="add-custom-endpoint-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Endpoint
          </Button>
        </CardHeader>
        <CardContent>
          {endpoints.length > 0 ? (
            <div className="space-y-3">
              {endpoints.map((ep) => (
                <div
                  key={ep.endpoint_id}
                  className="flex items-center justify-between p-4 bg-[var(--qn-background)] rounded-lg border border-[var(--qn-border)]"
                  data-testid={`endpoint-${ep.endpoint_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${
                      ep.health_status === "healthy" ? "bg-emerald-400" :
                      ep.health_status === "unhealthy" ? "bg-red-400" :
                      ep.health_status === "unreachable" ? "bg-red-400 animate-pulse" :
                      "bg-gray-400"
                    }`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{ep.name}</span>
                        <Badge variant="outline" className="text-xs border-[var(--qn-border)]">
                          {ep.qnneal_model_id}
                        </Badge>
                      </div>
                      <div className="text-xs text-[var(--qn-foreground-muted)] mt-0.5 font-mono">
                        {ep.endpoint_url}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--qn-foreground-subtle)]">
                      {ep.total_requests || 0} requests
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleHealthCheck(ep.endpoint_id)}
                      disabled={healthChecking[ep.endpoint_id]}
                      title="Health Check"
                    >
                      {healthChecking[ep.endpoint_id] ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTestEndpoint(ep.endpoint_id)}
                      disabled={testing[ep.endpoint_id]}
                      title="Test Endpoint"
                    >
                      {testing[ep.endpoint_id] ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleDeleteEndpoint(ep.endpoint_id)}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[var(--qn-foreground-muted)]">
              <Server className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No custom endpoints configured.</p>
              <p className="text-xs mt-1">Deploy Gemma 4 on your GPU and add the endpoint here.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deployment Guide */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-amber-500" />
            Quick Deployment Guide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-[var(--qn-background)] rounded-lg border border-[var(--qn-border)]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">vLLM Command (Gemma 4 27B MoE on A100)</span>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`python -m vllm.entrypoints.openai.api_server \\
    --model google/gemma-4-27B-it \\
    --dtype bfloat16 \\
    --quantization fp8 \\
    --gpu-memory-utilization 0.90 \\
    --max-model-len 16384 \\
    --port 8000`)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <pre className="text-xs font-mono text-[var(--qn-foreground-muted)] bg-black/20 p-3 rounded-lg overflow-x-auto">
{`python -m vllm.entrypoints.openai.api_server \\
    --model google/gemma-4-27B-it \\
    --dtype bfloat16 \\
    --quantization fp8 \\
    --gpu-memory-utilization 0.90 \\
    --max-model-len 16384 \\
    --port 8000`}
              </pre>
            </div>
            <div className="flex items-center gap-4 text-sm text-[var(--qn-foreground-muted)]">
              <a
                href="https://docs.vllm.ai/en/latest/serving/openai_compatible_server.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-[var(--qn-primary)] hover:underline"
              >
                vLLM Docs <ExternalLink className="w-3 h-3" />
              </a>
              <span>•</span>
              <span>Estimated cost: $0.03-0.08/1M tokens (A100 spot)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Endpoint Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Custom Endpoint</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">
              Register a self-hosted Gemma 4 or other OpenAI-compatible endpoint
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Endpoint Name</Label>
              <Input
                value={newEndpoint.name}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, name: e.target.value })}
                placeholder="My Gemma 4 Server"
                className="bg-[var(--qn-background)] border-[var(--qn-border)]"
                data-testid="endpoint-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Endpoint URL</Label>
              <Input
                value={newEndpoint.endpoint_url}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, endpoint_url: e.target.value })}
                placeholder="http://gpu-server:8000"
                className="bg-[var(--qn-background)] border-[var(--qn-border)]"
                data-testid="endpoint-url-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">API Key (optional)</Label>
              <Input
                type="password"
                value={newEndpoint.api_key}
                onChange={(e) => setNewEndpoint({ ...newEndpoint, api_key: e.target.value })}
                placeholder="Leave blank if not required"
                className="bg-[var(--qn-background)] border-[var(--qn-border)]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">vLLM Model ID</Label>
                <Input
                  value={newEndpoint.model_id}
                  onChange={(e) => setNewEndpoint({ ...newEndpoint, model_id: e.target.value })}
                  placeholder="google/gemma-4-27b-it"
                  className="bg-[var(--qn-background)] border-[var(--qn-border)]"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">QNNEAL Model ID</Label>
                <Select
                  value={newEndpoint.qnneal_model_id}
                  onValueChange={(v) => setNewEndpoint({ ...newEndpoint, qnneal_model_id: v })}
                >
                  <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                    {GEMMA4_MODELS.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Input Cost ($/1M)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newEndpoint.cost_per_million_input}
                  onChange={(e) => setNewEndpoint({ ...newEndpoint, cost_per_million_input: parseFloat(e.target.value) })}
                  className="bg-[var(--qn-background)] border-[var(--qn-border)]"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Output Cost ($/1M)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newEndpoint.cost_per_million_output}
                  onChange={(e) => setNewEndpoint({ ...newEndpoint, cost_per_million_output: parseFloat(e.target.value) })}
                  className="bg-[var(--qn-background)] border-[var(--qn-border)]"
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-[var(--qn-background)] rounded-lg">
              <div>
                <div className="text-sm font-medium">Thinking Mode</div>
                <div className="text-xs text-[var(--qn-foreground-muted)]">Enable &lt;|think|&gt; prefix for reasoning tasks</div>
              </div>
              <Switch
                checked={newEndpoint.thinking_mode}
                onCheckedChange={(v) => setNewEndpoint({ ...newEndpoint, thinking_mode: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddEndpoint} className="btn-primary" data-testid="confirm-add-endpoint-btn">
              Add Endpoint
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
